import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface VersionDiffItem {
  name: string;
  note?: string;
}

// General (non-structural) bug fixes/enhancements called out on a version — e.g. a patch release
// like 3.0.1 that adds/removes no component, style option, or SDK option.
export interface VersionUpdateGroup {
  version: string;
  items: string[];
}

export interface VersionDiff {
  from: string;
  to: string;
  components: { add: VersionDiffItem[]; remove: VersionDiffItem[] };
  styles: { add: VersionDiffItem[]; remove: VersionDiffItem[] };
  options: { add: VersionDiffItem[]; remove: VersionDiffItem[] };
  updates: VersionUpdateGroup[];
}

export interface SdkVersionsState {
  components: string[] | undefined;
  componentsVersion: string | null;
  isComponentsLoading: boolean;
  versionDiff: VersionDiff | null;
  isVersionDiffLoading: boolean;
  versionDiffError: string | null;
}

const initialSdkVersionsState: SdkVersionsState = {
  components: undefined,
  componentsVersion: null,
  isComponentsLoading: false,
  versionDiff: null,
  isVersionDiffLoading: false,
  versionDiffError: null
};

// components/componentsVersion track the /hosted-pages/getcomponentlist result for whichever
// brand's SdkVersion was last requested (DesignComponent). currentJsVersion/allJsVersions live in
// state.auth.authConfig — fetched via /config (backend-cached CDN version-chain manifest).
export const SdkVersionsSlice = createSlice({
  name: "sdkVersions",
  initialState: initialSdkVersionsState,
  reducers: {
    getSdkComponentsRequest(state, action: PayloadAction<string>) {
      state.isComponentsLoading = true;
      state.componentsVersion = action.payload;
    },
    getSdkComponentsSuccess(state, action: PayloadAction<{ version: string; components: string[] }>) {
      state.isComponentsLoading = false;
      state.components = action.payload.components;
      state.componentsVersion = action.payload.version;
    },
    getSdkComponentsFailure(state) {
      state.isComponentsLoading = false;
      state.components = undefined;
    },
    getVersionDiffRequest(state, _action: PayloadAction<{ from: string; to: string }>) {
      state.isVersionDiffLoading = true;
      state.versionDiffError = null;
      state.versionDiff = null;
    },
    getVersionDiffSuccess(state, action: PayloadAction<VersionDiff>) {
      state.isVersionDiffLoading = false;
      state.versionDiff = action.payload;
    },
    getVersionDiffFailure(state, action: PayloadAction<string>) {
      state.isVersionDiffLoading = false;
      state.versionDiffError = action.payload;
    },
    resetVersionDiff(state) {
      state.versionDiff = null;
      state.isVersionDiffLoading = false;
      state.versionDiffError = null;
    }
  }
});

export const { getSdkComponentsRequest, getSdkComponentsSuccess, getSdkComponentsFailure, getVersionDiffRequest, getVersionDiffSuccess, getVersionDiffFailure, resetVersionDiff } =
  SdkVersionsSlice.actions;

export default SdkVersionsSlice.reducer;
